/**
 * The appearance vocabulary: which themes exist, which modes each has, and the
 * two reading dials that sit beside them.
 *
 * A THEME is a complete token set — a file in `web/src/styles/themes/` (or the
 * `:root` contract in `tokens.css` for `terys`). A MODE is the light or dark
 * half of one theme. The pair is one fact, chosen together in the picker,
 * because "dark" is not a global truth: each theme ships its own dark values.
 *
 * A SCALE and a DENSITY are the other two axes of the same choice, and they are
 * deliberately separate from each other. Scale moves the root font size, so
 * every measure in the product — type, spacing, panel widths, glyphs, all of
 * which are `rem` — follows it in proportion; it answers "the text is too
 * small" and "the interface is too big" without changing any layout's shape.
 * Density moves the spacing scale ALONE, so the same type sits in tighter or
 * airier rows; it answers "I want more rows on this screen". Folding the two
 * into one dial would mean a clinician who wants larger text has to accept
 * fewer rows, which is the opposite of a preference.
 *
 * This module is the single list the stylesheet layer is checked against:
 * `web/tests/style-themes.test.ts` fails when `THEMES` and the files in
 * `styles/themes/` disagree in either direction, and
 * `web/tests/style-appearance.test.ts` fails when `SCALES` or `DENSITIES`
 * name a value `styles/appearance.css` does not implement.
 */

export const THEMES = ['terys', 'emerald', 'amber', 'graphite', 'smartomica', 'smartomicabrand'] as const
export type ThemeId = (typeof THEMES)[number]

export const MODES = ['light', 'dark'] as const
export type ModeId = (typeof MODES)[number]

/**
 * The reading scale, as a percentage of the browser's own text size.
 *
 * FOUR STEPS, NOT A SLIDER. Every step has to be a size the product was
 * actually looked at, and a continuous control invites the halves and quarters
 * between them that nobody has seen. The range is deliberately narrow at the
 * bottom: below 85% the clinical tables stop being comfortably readable at arm's
 * length on a ward workstation, and a dial that offers 50% offers a setting the
 * product cannot honour. 110% is the other direction — the same product for
 * someone who wants it larger, which is the request behind most zoom presses.
 *
 * The browser's own zoom remains available and is unaffected by this: zoom
 * scales the viewport, so the layout reaches its breakpoints sooner, while this
 * scales the type inside a viewport that keeps its size.
 */
export const SCALES = [85, 90, 100, 110] as const
export type ScaleId = (typeof SCALES)[number]

/**
 * How tightly the product packs its rows, at whatever scale.
 *
 * `cozy` is the product as it has always been drawn, and it is the default for
 * that reason rather than as a compromise between the other two.
 */
export const DENSITIES = ['compact', 'cozy', 'relaxed'] as const
export type DensityId = (typeof DENSITIES)[number]

export interface Appearance {
  theme: ThemeId
  mode: ModeId
  scale: ScaleId
  density: DensityId
}

/**
 * What the product renders for an account that has not chosen: amber dark at
 * the drawn size, by operator decision.
 *
 * A default is a choice the product makes, not the absence of one. The theme
 * and the mode are stamped on the document element like any other appearance —
 * `web/index.html` ships them already stamped, because the first paint happens
 * before React mounts and an unstamped document would show the light contract
 * for a frame and then flip. `web/tests/style-themes.test.ts` fails when the
 * document and this constant disagree; nothing else would notice a wrong first
 * paint.
 *
 * The two dials need no such stamp, and that is a property of how they are
 * built rather than an omission: `styles/appearance.css` states the DEFAULTS AS
 * THE UNSTAMPED CASE, so a document carrying neither attribute is already at
 * 100% and cozy. An account that chose otherwise repaints once, exactly like an
 * account that chose a theme other than the default.
 *
 * There is consequently no "follow the operating system" state any more:
 * following the OS meant stamping nothing, and nothing is a state the product
 * no longer shows. The OS-following block in `tokens.css` survives as what an
 * unstamped document falls back to, not as a product default.
 */
export const DEFAULT_APPEARANCE: Appearance = {
  theme: 'amber',
  mode: 'dark',
  scale: 100,
  density: 'cozy',
}

/**
 * The scope under which the choice is stored in the account UI-settings store
 * (`PUT /api/v1/account/ui-settings`). One row per account: the appearance is
 * a property of the person, not of a screen, so the scope key stays null.
 *
 * ONE ROW FOR ALL FOUR AXES. The print-form editor and the media editor read
 * this same row across all four axes (`pfe/src/app/shellState.ts`,
 * `mpe/src/appearance.ts`), so a clinician's appearance stays one fact with
 * one save rather than three rows that can disagree.
 */
export const UI_SETTINGS_APPEARANCE_SCOPE = 'appearance'

export function isTheme(value: unknown): value is ThemeId {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value)
}

export function isMode(value: unknown): value is ModeId {
  return typeof value === 'string' && (MODES as readonly string[]).includes(value)
}

export function isScale(value: unknown): value is ScaleId {
  return typeof value === 'number' && (SCALES as readonly number[]).includes(value)
}

export function isDensity(value: unknown): value is DensityId {
  return typeof value === 'string' && (DENSITIES as readonly string[]).includes(value)
}

export function encodeAppearancePayload(value: Appearance | null): Record<string, unknown> {
  // `null` never reaches the wire in practice — a save only follows a choice —
  // but encoding it as the empty payload keeps the round trip total: an empty
  // payload decodes back to "no choice" below.
  return value
    ? { theme: value.theme, mode: value.mode, scale: value.scale, density: value.density }
    : {}
}

/**
 * Reads a stored payload back into a choice, or `null` when there is none.
 *
 * Deliberately a validator and not a cast: the store keeps whatever the last
 * writer sent, including a payload from a NEWER build that knows themes this
 * one does not. An unrecognised value must degrade rather than stamp an
 * attribute no stylesheet defines, which would render the product in the token
 * contract's light values with a `color-scheme` the OS may contradict.
 *
 * THE THEME AND MODE ARE THE GATE; THE DIALS ARE FILLED IN. Every row written
 * before the dials existed holds a valid pair and no scale, and reading those
 * as "nothing chosen" would silently discard the theme every existing account
 * has picked. An unreadable dial therefore falls back to its default beside a
 * theme that stands, while an unreadable theme is still no choice at all —
 * because a theme is the one field with nothing safe to fall back to.
 */
export function decodeAppearancePayload(payload: Record<string, unknown>): Appearance | null {
  const theme = payload['theme']
  const mode = payload['mode']
  if (!isTheme(theme) || !isMode(mode)) {
    return null
  }
  const scale = payload['scale']
  const density = payload['density']
  return {
    theme,
    mode,
    scale: isScale(scale) ? scale : DEFAULT_APPEARANCE.scale,
    density: isDensity(density) ? density : DEFAULT_APPEARANCE.density,
  }
}

/**
 * The theme and mode as one string, `theme:mode`.
 *
 * What the CLOSED control states, in a form a test can read without
 * interpreting a picture. It is not a wire format and nothing parses it back:
 * the picker sets each axis on its own.
 */
export function appearanceOptionValue(value: Appearance): string {
  return `${value.theme}:${value.mode}`
}
